import { Form, Formik, FormikHelpers } from "formik";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";

import LoadingIndicator from "src/components/LoadingIndicator";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import { Role } from "src/graphql/generated/globalTypes";
import { getErrorMessage } from "src/util/errors";

import { Admin } from "./types";
import useRoleSelector, { FormikRoleSelector } from "./useRoleSelector";

const schema = yup.object().shape({
  role: yup.string().required("Required")
});

export type Values = { role: Role };

const useStyles = makeStyles(theme => ({
  errorAlert: {
    marginBottom: theme.spacing(3)
  },
  content: {
    width: "600px"
  }
}));

export default function ChangeRoleDialog({
  admin,
  onClose,
  handleChangeRole
}: {
  admin: Admin | null;
  onClose: () => void;
  handleChangeRole: (adminId: string, role: Role) => Promise<void>;
}): JSX.Element {
  const snackbar = useSnackbar();

  const [serverError, setServerError] = useState<string | null>(null);

  async function handleSubmit(values: Values, helpers: FormikHelpers<Values>) {
    setServerError(null);
    try {
      await handleChangeRole(admin!.id, values.role);
      snackbar.enqueueSnackbar("Role updated", { variant: "info" });
      helpers.resetForm();
      onClose();
    } catch (error) {
      setServerError(getErrorMessage(error));
    }
  }
  return (
    <Dialog open={!!admin} aria-labelledby="alert-dialog-title">
      {admin && (
        <ChangeRoleDialogForm
          admin={admin}
          onClose={() => {
            setServerError(null);
            onClose();
          }}
          handleSubmit={handleSubmit}
          serverError={serverError}
        />
      )}
    </Dialog>
  );
}

function ChangeRoleDialogForm({
  admin,
  onClose,
  handleSubmit,
  serverError
}: {
  admin: Admin;
  onClose: () => void;
  handleSubmit: (
    values: Values,
    helpers: FormikHelpers<Values>
  ) => Promise<void>;
  serverError: string | null;
}): JSX.Element {
  const classes = useStyles();
  const { defaultRoleSelection, roleSelections } = useRoleSelector();

  return (
    <Formik
      initialValues={{
        role: defaultRoleSelection as Role
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ isSubmitting, resetForm }) => (
        <Form>
          <DialogTitle id="alert-dialog-title">Change Admin Role</DialogTitle>
          <DialogContent className={classes.content}>
            <p>
              Choose a new role for <strong>{admin.fullName}</strong> (
              {admin.email}):
            </p>
            {isSubmitting ? (
              <LoadingIndicator />
            ) : (
              <>
                <FormikErrorBanner additionalErrors={serverError} />
                <FormikRoleSelector
                  roleSelections={roleSelections}
                  name="role"
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={isSubmitting}
              onClick={() => {
                resetForm();
                onClose();
              }}
              color="primary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              color={"primary"}
              variant="contained"
            >
              {isSubmitting ? "Changing..." : "Change Role"}
            </Button>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
}
